// import hooks
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// import types
import { CLA_SETTINGS_FLAG } from '../CLASettings.types';

// import utils
import { isFieldInArrayValueGetter } from 'components/shared/DataGrid/utils/valueGetters/isFieldInArrayValueGetter';

// import components
import { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';
import BooleanCell from 'components/shared/Table/TableCell/DataGrid/BooleanCell';

export const useColumnDefinitions = (): GridColDef[] => {
  const { t } = useTranslation('common', { keyPrefix: 'adminPortal.cla' });

  const commonColumnProps = {
    flex: 1,
    headerAlign: 'left',
    align: 'left'
  } as const;

  return useMemo<GridColDef<GridValidRowModel>[]>(
    () => [
      {
        field: 'code',
        headerName: t('code'),
        type: 'string',
        hideable: false,
        ...commonColumnProps
      },
      {
        field: 'description',
        headerName: t('description'),
        type: 'string',
        ...commonColumnProps
      },
      {
        field: 'year',
        headerName: t('year'),
        type: 'string',
        ...commonColumnProps
      },
      {
        field: 'hoursWorkingPerYear',
        headerName: t('yearHours'),
        type: 'number',
        ...commonColumnProps
      },
      {
        field: 'periodHours',
        headerName: t('periodHours'),
        type: 'number',
        ...commonColumnProps
      },
      {
        field: 'hoursHolidayPerYear',
        headerName: t('leave'),
        type: 'number',
        ...commonColumnProps
      },
      {
        field: 'hoursWorkingPerWeek',
        headerName: t('weekHours'),
        type: 'number',
        ...commonColumnProps
      },
      {
        field: 'hoursWorkingPerDay',
        headerName: t('dayHours'),
        type: 'number',
        ...commonColumnProps
      },
      {
        field: 'LEAVE_HOURS_EQUAL_PERIODS' as CLA_SETTINGS_FLAG,
        type: 'boolean',
        headerName: t('leaveEqualPeriods'),
        valueGetter: (_, row, column) =>
          isFieldInArrayValueGetter('flags')(column.field, row),
        renderCell: ({ value }) => <BooleanCell value={value} />,
        ...commonColumnProps
      },
      {
        field: 'leaveHoursBasedOnWork',
        type: 'boolean',
        headerName: t('leaveBasedOnWork'),
        renderCell: ({ value }) => <BooleanCell value={value} />,
        ...commonColumnProps
      }
    ],
    []
  );
};
